import { supabase } from './client';
import { authStore } from '@stores/session';

// The generated Database types are a snapshot that predates migration
// 0050, so the new table isn't in them yet — use the same `as any` escape
// hatch the other user-owned table clients use (watchlist / portfolio).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// Per-patient brand exclusions (migration 0050). Keyed on the brand
// STRING (there is no brands entity — brand is free text on strains).
// Drives the /settings multiselect, the listing-page exclusion filter,
// and the watchlist / next-script warning pill.

/** The set of brands the current user has hidden. Empty when signed out. */
export async function fetchExcludedBrands(): Promise<Set<string>> {
  const profile = authStore.profile();
  if (!profile) return new Set();
  const { data, error } = await db
    .from('user_excluded_brands')
    .select('brand')
    .eq('profile_id', profile.id);
  if (error) throw error;
  return new Set(((data ?? []) as { brand: string }[]).map((r) => r.brand));
}

export async function addExcludedBrand(brand: string): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const { error } = await db
    .from('user_excluded_brands')
    .insert({ profile_id: profile.id, brand });
  // 23505 = already excluded; treat as a no-op so a double-click is safe.
  if (error && error.code !== '23505') throw error;
}

export async function removeExcludedBrand(brand: string): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const { error } = await db
    .from('user_excluded_brands')
    .delete()
    .eq('profile_id', profile.id)
    .eq('brand', brand);
  if (error) throw error;
}

/**
 * Distinct brand names across the catalogue, for the settings picker.
 * strains is public-read, so this works signed-out too (though the
 * exclusion UI itself is auth-gated).
 */
export async function fetchAllBrands(): Promise<string[]> {
  const { data, error } = await supabase
    .from('strains')
    .select('brand')
    .order('brand');
  if (error) throw error;
  const set = new Set<string>();
  for (const r of (data ?? []) as { brand: string }[]) {
    if (r.brand) set.add(r.brand);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}
